/**
 * Utility functions for managing color scheme preferences
 */

export const THEME_STORAGE_KEY = 'color-scheme-preference';
export const DEFAULT_THEME = 'light';

/**
 * Gets the system preference for color scheme
 * @returns {boolean} true if system prefers dark color scheme
 */
export const getSystemPreference = () => {
	if (typeof window === 'undefined') return false;
	return window.matchMedia('(prefers-color-scheme: dark)').matches;
};

/**
 * Applies color scheme to the document
 * @param {boolean} isDark - whether to apply dark color scheme
 */
export const applyColorScheme = isDark => {
	if (typeof document === 'undefined') return;
	const root = document.documentElement;
	if (isDark) {
		root.classList.add('dark');
	} else {
		root.classList.remove('dark');
	}
};

/**
 * Gets the stored color scheme preference from localStorage
 * @returns {string} 'light', 'dark', or 'system'
 */
export const getStoredPreference = () => {
	if (typeof window === 'undefined') return DEFAULT_THEME;
	return localStorage.getItem(THEME_STORAGE_KEY) || DEFAULT_THEME;
};

/**
 * Determines if the color scheme should be dark based on the preference
 * @param {string} preference - 'light', 'dark', or 'system'
 * @returns {boolean} true if dark scheme should be applied
 */
export const shouldUseDarkScheme = preference => {
	if (preference === 'dark') return true;
	if (preference === 'light') return false;
	// system mode
	return getSystemPreference();
};

/**
 * Initializes the color scheme from localStorage on app load
 * This should be called before React renders to prevent flash of wrong theme
 */
export const initializeColorScheme = () => {
	const stored = getStoredPreference();
	const isDark = shouldUseDarkScheme(stored);
	applyColorScheme(isDark);
};
